import L from "@/modules/login/Login.module.css";

import { useConfig } from "@/config.ts";
import { canSelectApiPrefix } from "@/modules/api/createApiClient.ts";
import type { Translatable } from "@/modules/l10n/getTransText.ts";
import { useForceLightMode } from "@/modules/main/ColorSchemeSwitcher.store.ts";
import { hasDebugBarAtom } from "@/modules/main/debug/DebugMenu.store.ts";
import { useThemeProps } from "@/modules/theme/useTheme.ts";
import { cn } from "@/utils/classNames.ts";
import { isStorybook } from "@/utils/environment.ts";
import { useTitle } from "@/utils/react/useTitle.ts";
import { Trans } from "@lingui/react/macro";
import { filterFalse } from "@warrenio/utils/collections/filterFalse";
import { useAtomValue } from "jotai";
import type { ReactNode } from "react";
import React from "react";

const SiteThemeSelect = React.lazy(() => import("@/modules/main/debug/SiteThemeSelect.tsx"));
const ApiPrefixSelect = React.lazy(() => import("@/modules/main/debug/ApiPrefixSelect.tsx"));

const canSelectTheme = !import.meta.env.PROD && !isStorybook;

export function LoginLayout({ children }: { children: ReactNode }) {
    // Login components do not currently support dark mode properly (missing text styles)
    useForceLightMode();

    const { loginIntroImg, loginIntro } = useThemeProps();
    const { siteName, loginTitle, loginSubtitle } = useConfig();

    return (
        <>
            <div className={cn(L.Login, "size-full")}>
                <div className={L.Intro} {...loginIntro}>
                    {loginIntroImg && <img alt={siteName} fetchPriority="low" {...loginIntroImg} />}
                    {loginTitle === "Log in" ? (
                        <h1 className={L.Title}>
                            <Trans>Log in</Trans>
                        </h1>
                    ) : (
                        !!loginTitle && (
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: These come from our own theme so safe
                            <h1 className={L.Title} dangerouslySetInnerHTML={{ __html: loginTitle }} />
                        )
                    )}
                    {!!loginSubtitle && (
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: (see above)
                        <h2 className={L.Subtitle} dangerouslySetInnerHTML={{ __html: loginSubtitle }} />
                    )}
                </div>

                {children}
            </div>
            <DebugControls />
        </>
    );
}

export function LoginBox({ title, children }: { title: Translatable; children: ReactNode }) {
    useTitle(title);

    const { loginLogoImg, loginFormBefore, loginForm, loginFormInner } = useThemeProps();
    const { siteName } = useConfig();

    return (
        <div className={L.Form} {...loginForm}>
            <div className={L.Before} {...loginFormBefore} />

            <div className={L.Inner} {...loginFormInner}>
                {loginLogoImg && (
                    <div className={L.Logo}>
                        <img alt={siteName} {...loginLogoImg} />
                    </div>
                )}

                <h2 className={cn(L.Title, "text-center")}>{title}</h2>

                {children}
            </div>
        </div>
    );
}

function DebugControls() {
    const hasDebugBar = useAtomValue(hasDebugBarAtom);
    const components = filterFalse([
        canSelectTheme && <SiteThemeSelect key="SiteThemeSelect" />,
        canSelectApiPrefix && <ApiPrefixSelect key="ApiPrefixSelect" />,
    ]);

    return (
        hasDebugBar &&
        components.length && (
            <div className="absolute HStack gap-2" style={{ top: "10px", left: "10px" }}>
                {components}
            </div>
        )
    );
}
